import { Injectable, InjectionToken } from '@angular/core';
import { JsonataAction } from './actions/jsonata-action';
import { EqualityCondition } from './conditions/equality-condition';
import { JsonataCondition } from './conditions/jsonata-condition';
import { Condition, Action, PartialRule, Rule, Data } from './model/rule-engine.model';

/**
 * Base abstract class for rule factories
 */
export abstract class AbstractRuleFactory<T = Data> {
  abstract getRuleInstance(rule: PartialRule<T>): Rule<T>;
  abstract _getCondition(condition: Pick<Condition<T>, 'params' | 'type'>): Condition<T>;
  abstract _getAction(action: Pick<Action<T>, 'params' | 'type'>): Action<T>;
}

/**
 * Token to be used while injecting AbstractRuleFactory
 */
export const RULE_FACTORY_SERVICE = new InjectionToken<AbstractRuleFactory>('RuleFactoryService');

/**
 * Base rule factory service, it receives PartialRule definition (ids and names) and return
 * instance of each definition
 */
@Injectable()
export class RuleFactoryService<T> extends AbstractRuleFactory<T> {
  /**
   * Build a {@link Rule} based on `type` & `params` values of given {@link PartialRule}
   * @param rule
   * @returns
   */
  getRuleInstance(rule: PartialRule<T>): Rule<T> {
    rule.when = rule.when.map(this._getCondition).filter(Boolean);
    rule.then = rule.then.map(this._getAction).filter(Boolean);
    return rule as Rule<T>;
  }

  /**
   * Parses a {@link Condition} implementation based on type and params within given condition
   * @param condition
   * @returns
   */
  _getCondition = <T>(condition: Pick<Condition<T>, 'params' | 'type'>): Condition<T> => {
    if (condition.type === 'jsonata') return new JsonataCondition(condition);
    if (condition.type === 'isEqualTo') return new EqualityCondition(condition);
    return null;
  };

  /**
   * Parses a {@link Action} implementation based on type and params within given action
   * @param action
   * @returns
   */
  _getAction = (action: Pick<Action<T>, 'params' | 'type'>): Action<T> => {
    if (action.type === 'jsonata') return new JsonataAction(action);
    return null;
  };
}
